/** @module csi/view/ViewDialog */
define([
	"dojo/_base/declare",
	"dojo/dom-geometry",
	"dojo/dom-style",
	"dojo/keys",
	"dojo/query",
	"dojo/topic",
	"dojo/window",
	"underscore",
	"csi/html",
	"csi/state",
	"csi/widget/Dialog",
	"dojo/text!./ViewDialog.html",
], function(declare, domGeometry, domStyle, keys, query, topic, winUtils, _, html, state, Dialog, template) {

	return declare(Dialog, {
		templateString: template,
		baseClass: "csi-view-dialog-wrapper",

		_handles: null,

		/** @property {boolean} bMaxSize - set true to make dialog always be 95% of viewport size
		 * in this case viewDialog.contentNode has set width and height and content must occupy all available space
		 */
		bMaxSize: false,

		/** @property {boolean} bSubmitOnEnter - set true to enable submit on pressing ENTER */
		bSubmitOnEnter: false,

		/** @property {string} closeTitle - title to use for dialog title bar close button (this is used in place of buttonCancel in Dojo which is not easy to override) */
		closeTitle: "Close",

		/** @property {boolean} showInvisibleOnStartup - show dialog on startup, but make invisible until "show" function is called explicitly signaling the content is ready */
		showInvisibleOnStartup: false,

		/**
		 * Resize dialog and its content without timeout (to prevent flicker);
		 */
		forceResize: function() {
			state.set("forceResize", true);
			this.resize();
			state.set("forceResize", false);
		},

		/**
		 * @override
		 * Reset event handles before hiding
		 */
		hide: function() {
			this.stopEvents();
			return this.inherited(arguments);
		},

		/**
		 * @override
		 * If so configured, show dialog on startup after view has loaded so DOM calculations will work, but still make invisible so
		 * user will not see any jitter while calculations are done
		 */
		startup: function() {
			if (!this._started) {
				this.inherited(arguments);

				if (this.showInvisibleOnStartup) {
					html.setVisibility(this.domNode, false);
					Dialog.prototype.show.call(this);
				}
			}
		},

		resize: function(dim) {
			// summary: resize and position dialog
			if (this.domNode.style.display != "none") {
				const {bMaxSize, containerNode, outerContainerNode} = this;
				const scrollNode = this._getViewScrollNode();

				if (bMaxSize) {
					var containerBox = domGeometry.getContentBox(containerNode);
					var viewBox = domGeometry.getContentBox(outerContainerNode);
					var viewport = winUtils.getBox(this.ownerDocument);
					var ratio = 0.95; // 95% of viewport size; use this._getViewportRatio() for 90%/100% auto like other dialogs
					viewport.w *= ratio;
					viewport.h *= ratio;

					var height = viewport.h - (viewBox.h - containerBox.h) - 2;
					var width = viewport.w - (viewBox.w - containerBox.w) - 2;

					domStyle.set(this.containerNode, {
						height: height + "px",
						width: width + "px",
						"overflow": ""
					});
				} else if (scrollNode) {
					// set scrollNode max-height/width so view does not exceed size of dialog
					const viewport = winUtils.getBox(this.ownerDocument);
					const ratio = this._getViewportRatio();
					viewport.w *= ratio;
					viewport.h *= ratio;

					const fixedHeight = outerContainerNode.offsetHeight - scrollNode.offsetHeight;
					const fixedWidth = outerContainerNode.offsetWidth - scrollNode.offsetWidth;

					// calculate max width and height of the scroll region
					// NOTE: Not sure why we subtract 2 more pixels, but keeping to be consistent with past
					const maxHeight = Math.max(viewport.h - fixedHeight - 2, 100);
					const maxWidth = Math.max(viewport.w - fixedWidth - 2, 300);

					html.setPixels(scrollNode, html.MAXHEIGHT, maxHeight);
					html.setPixels(scrollNode, html.MAXWIDTH, maxWidth);

					// undo what is done
					domStyle.set(containerNode, "overflow", "");
				} else {
					// if no scrollNode, then set container overflow to auto - not done by inherited resize if only one child
					domStyle.set(containerNode, "overflow", "auto");
				}

				// We are not calling inherited to prevent default behavior of dijitDialog.resize()
				// We don't want it because we now using CSS to position and size dialog.
				//this.inherited(arguments);

				// proceed with children resize
				this._checkIfSingleChild();
				if (this._singleChild) {
					this._singleChild.resize();
				} else {
					this._layoutChildren();
				}
			}
		},

		/**
		 * @override
		 * Make dialog visible if not so before, and listen for resize events
		 */
		show: function() {
			// show contents after load
			if (this.showInvisibleOnStartup) {
				html.setVisibility(this.domNode, true);
			}

			// on first layout start listening for events that could affect size
			this._handles = this._handles || [
				topic.subscribe("/csi/dialog/resize/required", (bForce) => {
					if (bForce) {
						this.forceResize();
					} else {
						this.resize();
					}
				})
			];

			this.inherited(arguments);
		},

		stopEvents: function() {
			// summary: cleanup event handles
			_.each(this._handles, function(h) {h.remove();});
			this._handles = null;
		},

		/**
		 * Return node which view uses for scrolling
		 * @return {DOMNode}
		 */
		_getViewScrollNode: function() {
			const view = this.getChildren().last();
			return view.scrollNode;
		},

		/**
		 * Submit on Enter
		 */
		_onKey: function(ev) {
			if ((ev.keyCode == keys.ENTER) && this.bSubmitOnEnter) {
				this._onSubmit(ev);
				return;
			}
			this.inherited(arguments);
		},

		/**
		 * Override default function, do nothing instead
		 */
		_position: function() {}

	});
});